import type { TypedDocumentNode } from "urql";

import type { DrgCreateAnnotationInput } from "../../remote/gql/graphql";
import type { AlaffiaClientModuleArgs } from "./modules";

import { drgCreateAnnotationMutationDoc } from "../../remote/mutations/drgCreateAnnotation.gql";
import { drgCreateFindingsMutationDoc } from "../../remote/mutations/drgCreateFindings.gql";
import { drgDeleteAnnotationMutationDoc } from "../../remote/mutations/drgDeleteAnnotation.gql";
import { drgSetFindingDispositionMutationDoc } from "../../remote/mutations/drgSetFindingDisposition.gql";
import { drgUpdateFindingMutationDoc } from "../../remote/mutations/drgUpdateFinding.gql";
import { AlaffiaClientModule } from "./modules";

// Mirrors GraphQL `input DrgAnnotationCoordinateInput`.
export interface DrgAnnotationCoordinateInput {
  left: number;
  top: number;
  width: number;
  height: number;
}

// Mirrors GraphQL `input DrgAnnotationInput`.
export interface DrgAnnotationInput {
  id?: string | null;
  page: number;
  fileId?: string | null;
  fileName?: string | null;
  citationKey?: string | null;
  quote?: string | null;
  sourceText?: string | null;
  coordinates?: DrgAnnotationCoordinateInput[];
}

// Mirrors GraphQL `input DrgEvidenceItemInput`.
interface DrgEvidenceItemInput {
  id?: string | null;
  kind: string;
  title: string;
  description: string;
  sourceType: string;
  evidenceDate?: string | null;
  citationPage?: number | null;
  disqualifiesAs?: string | null;
  annotations?: DrgAnnotationInput[];
}

interface DrgProposedAddedCodeInput {
  code: string;
  role: string;
  rationale?: string | null;
}

interface DrgRepositionInput {
  code: string;
  fromPosition: number;
  toPosition: number;
}

interface DrgProposedChangeInput {
  removeCodes: string[];
  addCodes: DrgProposedAddedCodeInput[];
  reposition: DrgRepositionInput[];
}

// Mirrors GraphQL `input DrgCreateFindingInput`.
export interface DrgCreateFindingInput {
  claimId: string;
  agentRunId?: string | null;
  module: string;
  verdict: string;
  code: string;
  codeDescription: string;
  detectedRole?: string | null;
  confidence?: number | null;
  poaValue: string;
  poaCitationPage?: number | null;
  agentName?: string;
  agentDiagnosisType?: string;
  agentGovernedCodes?: string[];
  clinicalFindings?: string;
  aiNarrative?: string;
  supportingEvidence?: DrgEvidenceItemInput[];
  disqualifyingEvidence?: DrgEvidenceItemInput[];
  negatedSupportingEvidence?: DrgEvidenceItemInput[];
  negatedDisqualifyingEvidence?: DrgEvidenceItemInput[];
  proposedChange?: DrgProposedChangeInput | null;
}

export interface DrgCreateFindingsInput {
  claimId: string;
  agentRunId?: string | null;
  findings: DrgCreateFindingInput[];
}

// Mirrors GraphQL `input DrgUpdateFindingInput`.
export interface DrgUpdateFindingInput {
  findingId: string;
  finding: DrgCreateFindingInput;
}

// Mirrors GraphQL `type DrgEvidenceItem` (annotations now live on
// `DrgFinding.linkedAnnotations` / `DrgClaim.annotations` — not on the
// evidence item itself).
interface DrgEvidenceItem {
  id: string;
  kind: string;
  title: string;
  description: string;
  sourceType: string;
  evidenceDate?: string | null;
  citationPage?: number | null;
  disqualifiesAs?: string | null;
  aiTitle?: string | null;
  aiDescription?: string | null;
  aiEvidenceDate?: string | null;
  isPinned: boolean;
  pinnedAt?: string | null;
  negated: boolean;
}

interface DrgProposedAddedCode {
  code: string;
  role: string;
  rationale?: string | null;
}

interface DrgReposition {
  code: string;
  fromPosition: number;
  toPosition: number;
}

interface DrgProposedChange {
  removeCodes: string[];
  addCodes: DrgProposedAddedCode[];
  reposition: DrgReposition[];
}

export interface DrgFinding {
  id?: string | null;
  claimId: string;
  module: string;
  verdict: string;
  code: string;
  codeDescription: string;
  detectedRole?: string | null;
  confidence: number;
  poaValue: string;
  poaCitationPage?: number | null;
  agentName: string;
  agentDiagnosisType: string;
  agentGovernedCodes: string[];
  agentRunId?: string | null;
  aiNarrative: string;
  clinicalFindings?: string | null;
  disposition?: string | null;
  dispositionActor?: {
    id: string;
    email: string;
    displayName: string;
    orgId: string;
  } | null;
  dispositionAt?: string | null;
  // Assigned once by the server at creation time; never client-writable.
  // Human findings carry a DrgUser, agent findings a DrgAgent.
  author?:
    | { __typename: "DrgAgent"; id: string; name: string }
    | { __typename: "DrgUser"; id: string; displayName: string }
    | null;
  supportingEvidence: DrgEvidenceItem[];
  disqualifyingEvidence: DrgEvidenceItem[];
  proposedChange?: DrgProposedChange | null;
  pinnedEvidenceIds?: string[] | null;
  linkedAnnotationCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

type DrgModuleArgs = AlaffiaClientModuleArgs;

export class DrgModule extends AlaffiaClientModule {
  constructor(args: DrgModuleArgs) {
    super({ ...args, environment: "alafed" });
  }

  createFindings = async (input: DrgCreateFindingsInput) => {
    const result = await this.runGqlMutation(
      drgCreateFindingsMutationDoc as unknown as TypedDocumentNode<
        { drgCreateFindings: DrgFinding[] },
        { input: DrgCreateFindingsInput }
      >,
      { input },
      "drgCreateFindings",
    );

    return {
      data: result.data?.drgCreateFindings ?? null,
      error: result.error ?? null,
    };
  };

  updateFinding = async (input: DrgUpdateFindingInput) => {
    const result = await this.runGqlMutation(
      drgUpdateFindingMutationDoc as unknown as TypedDocumentNode<
        { drgUpdateFinding: DrgFinding },
        { input: DrgUpdateFindingInput }
      >,
      { input },
      "drgUpdateFinding",
    );

    return {
      data: result.data?.drgUpdateFinding ?? null,
      error: result.error ?? null,
    };
  };

  setFindingDisposition = async (input: {
    findingId: string;
    disposition: string;
  }) => {
    const result = await this.runGqlMutation(
      drgSetFindingDispositionMutationDoc,
      { input },
      "drgSetFindingDisposition",
    );

    return {
      data: result.data?.drgSetFindingDisposition ?? null,
      error: result.error ?? null,
    };
  };

  createAnnotation = async (input: DrgCreateAnnotationInput) => {
    const result = await this.runGqlMutation(
      drgCreateAnnotationMutationDoc,
      { input },
      "drgCreateAnnotation",
    );

    return {
      data: result.data?.drgCreateAnnotation ?? null,
      error: result.error ?? null,
    };
  };

  deleteAnnotation = async (input: { id: string }) => {
    const result = await this.runGqlMutation(
      drgDeleteAnnotationMutationDoc,
      { id: input.id },
      "drgDeleteAnnotation",
    );

    return {
      data: result.data?.drgDeleteAnnotation ?? null,
      error: result.error ?? null,
    };
  };
}
